import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, CheckCircle2, Radio, ArrowRight } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import Phase1Blocking from "@/components/pms/Phase1Blocking";
import Phase2Mapping from "@/components/pms/Phase2Mapping";
import BillingStep from "@/components/pms/BillingStep";

const STEPS = ["Unit connections", "Listing mapping", "Billing & activation"];

export default function PropertySetup() {
  const { pid } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [step, setStep] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/pms/properties/${pid}`);
      setD(data);
      if (step === null) {
        if (data.property.status === "active") setStep(3);
        else if (data.property.status === "payment_pending") setStep(2);
        else if (data.property.phase1_done) setStep(1);
        else setStep(0);
      }
      return data;
    } catch (e) { toast.error(errMsg(e)); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pid]);

  useEffect(() => { load(); }, [load]);

  if (!d || step === null) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-slate-300" size={32} /></div>;

  const { property, units } = d;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="backdrop-blur-xl bg-white/90 border-b border-slate-200/80 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button data-testid="back-to-pms-button" onClick={() => nav("/pms")} className="text-slate-400 hover:text-slate-700"><ArrowLeft size={18} /></button>
            <div>
              <h1 className="font-display font-bold text-slate-900 leading-tight">{property.name}</h1>
              <p className="text-[11px] text-slate-400">{property.city || "—"} · {units.length} units · ₹{(property.category || "homestay").toLowerCase() === "hotel" ? 300 : 475}/unit/month</p>
            </div>
          </div>
          {property.status === "active" ? (
            <div className="flex items-center gap-2">
              <span data-testid="pms-active-badge" className="text-[11px] font-semibold px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200 flex items-center gap-1.5 pulse-success"><CheckCircle2 size={12} /> PMS Active</span>
              <button data-testid="open-channel-wizard-button" onClick={() => nav(`/pms/property/${pid}/channels`)}
                className="btn-brand rounded-full px-4 py-2 text-xs font-semibold flex items-center gap-1.5"><Radio size={13} /> Channel Manager <ArrowRight size={12} /></button>
            </div>
          ) : (
            <span className="text-[11px] font-semibold px-3 py-1.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200">Setup in progress</span>
          )}
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 pt-8">
        <div className="flex items-center gap-2">
          {STEPS.map((s, i) => {
            const done = (i === 0 && property.phase1_done) || (i === 1 && ["payment_pending", "active"].includes(property.status)) || (i === 2 && property.status === "active");
            // Draft flow: step-0 always, step-1 after phase1_done, step-2 after payment.
            // Live property: only admins/co-admins can revisit step-0/1 to edit connections & mappings.
            const activeAdmin = d.can_edit_setup && property.status === "active";
            let clickable;
            if (i === 0) clickable = !property.phase1_done || activeAdmin;
            else if (i === 1) clickable = (property.phase1_done && property.status !== "active") || activeAdmin;
            else clickable = ["payment_pending", "active"].includes(property.status);
            return (
              <button key={s} data-testid={`setup-step-${i}`} disabled={!clickable} onClick={() => setStep(i)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold border transition-colors ${step === i ? "bg-brand text-white border-transparent" : done ? "bg-emerald-50 text-emerald-600 border-emerald-200" : "bg-white text-slate-400 border-slate-200"} ${!clickable ? "opacity-50 cursor-not-allowed" : ""}`}>
                {done && step !== i ? <CheckCircle2 size={13} /> : <span className="w-4 h-4 rounded-full bg-white/20 border border-current text-[9px] flex items-center justify-center">{i + 1}</span>}
                {s}
              </button>
            );
          })}
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {step === 0 && <Phase1Blocking data={d} reload={load} onDone={() => { setStep(1); }} />}
        {step === 1 && <Phase2Mapping data={d} reload={load} onDone={() => { setStep(2); }} />}
        {(step === 2 || step === 3) && <BillingStep data={d} reload={load} onActivated={() => setStep(3)} />}
      </main>
    </div>
  );
}
